import AwsS3Multipart from "@uppy/aws-s3-multipart";

// Защита отмены multipart-загрузки в @uppy/aws-s3-multipart@2.4.1.
//
// В проде (Loki, app=rentalman-api) дважды за две недели прилетал запрос
//
//   DELETE /clients/s3/multipart/null?key=null -> 404   (2026-08-23 04:01:36)
//   DELETE /s3/multipart/null?key=null         -> 404   (2026-08-26 21:28:32)
//
// Оба раза — Android WebView, то есть мобильная обёртка. `null` в пути и в
// query — это буквальный JS-null, прошедший через encodeURIComponent в
// AwsS3Multipart#abortMultipartUpload.
//
// 404 здесь — симптом. Настоящая цена — в логе 2026-08-23: там ОДИН успешный
// `POST /clients/s3/multipart` (200, тело с валидными uploadId/key), сразу за
// ним отмена с null/null — и больше ничего. Ни /batch, ни /complete, ни записи
// файла в API. У человека молча сорвалась загрузка документа клиента, а
// созданный на S3 multipart-upload остался висеть: отменить его было нечем,
// потому что настоящие key/uploadId уже потеряли.
//
// Почему потеряли — видно в исходниках библиотеки (lib/MultipartUploader.js):
//
//   #createUpload() {
//     this.createdPromise = Promise.resolve().then(() => createMultipartUpload())
//     return this.createdPromise.then(result => {
//       if (this.#aborted()) throw createAbortError()   // ← выход ДО присвоения
//       this.key = result.key
//       this.uploadId = result.uploadId
//       ...
//   }
//
//   #abortUpload() {
//     this.abortController.abort()                      // ← #aborted() = true
//     this.createdPromise.then(() => {
//       abortMultipartUpload({ key: this.key, uploadId: this.uploadId })
//     }, () => {})                                      // при отказе create — не отменяем
//   }
//
// Если отмена приходит, пока POST create ещё в полёте, порядок такой:
// abortController.abort() взводит флаг → create возвращает валидный ответ →
// первым на createdPromise отрабатывает колбэк из #createUpload и выбрасывает
// AbortError, НЕ дойдя до `this.key = ...` → вторым отрабатывает колбэк из
// #abortUpload и берёт поля, которые так и остались `null` из конструктора
// (`this.key = this.options.key || null`).
//
// Отменить загрузку в первые сотни миллисекунд — обычное дело в мобильной
// обёртке: hardware-back (AppHeader.vue вешает CapacitorApp backButton →
// $router.go(-1)) размонтирует компонент, beforeDestroy зовёт uppy.close(), а
// это cancelAll({reason: "user"}) → abort({really: true}).
//
// Чинить сам гонки в библиотеке нельзя (это её внутренние приватные поля), но
// результат create проходит через опции плагина — их и подменяем: запоминаем
// выданные сервером key/uploadId и на отмене подставляем их, если библиотека
// своих не донесла. Тогда отмена доходит до S3 по-настоящему, а не улетает
// строкой "null".
//
// Запрос без key/uploadId не отправляем вовсе: если create не успел ответить
// (или отказал), отменять на S3 нечего — там ещё ничего не создано.

const PLUGIN_ID = "AwsS3Multipart";

/**
 * Вешается на готовый инстанс Uppy сразу после .use(AwsS3Multipart, ...).
 *
 * @param {object} uppy инстанс @uppy/core
 * @returns {object} тот же инстанс — для цепочки
 */
export function guardMultipartAbort(uppy) {
  const plugin = uppy && uppy.getPlugin && uppy.getPlugin(PLUGIN_ID);
  if (!plugin || !plugin.opts) return uppy;

  // Идемпотентность: instantiateUppy в компонентах зовётся из mounted, но
  // повторный вызов на том же инстансе не должен наращивать цепочку обёрток.
  if (plugin.opts.__abortGuardInstalled) return uppy;

  const createdUploads = new Map();
  const createMultipartUpload = plugin.opts.createMultipartUpload;
  const abortMultipartUpload = plugin.opts.abortMultipartUpload;

  plugin.opts.createMultipartUpload = (file) =>
    Promise.resolve(createMultipartUpload(file)).then((result) => {
      if (result && result.key && result.uploadId) {
        createdUploads.set(file.id, { key: result.key, uploadId: result.uploadId });
      }
      return result;
    });

  plugin.opts.abortMultipartUpload = (file, data) => {
    const remembered = createdUploads.get(file.id);
    createdUploads.delete(file.id);

    const key = (data && data.key) || (remembered && remembered.key);
    const uploadId = (data && data.uploadId) || (remembered && remembered.uploadId);

    // Нечего отменять: create не ответил или отказал — на S3 загрузки нет.
    if (!key || !uploadId) return Promise.resolve();

    return abortMultipartUpload(file, { key, uploadId });
  };

  // Чистим память только на успехе. На file-removed чистить нельзя: плагин
  // подписывается на это же событие ПОЗЖЕ (в uploadFile), и наш обработчик
  // стёр бы запись раньше, чем она понадобится отмене.
  uppy.on("upload-success", (file) => {
    if (file) createdUploads.delete(file.id);
  });

  plugin.opts.__abortGuardInstalled = true;

  return uppy;
}

/**
 * Загрузчик multipart, у которого падение одного файла НЕ снимает пачку.
 *
 * FT-153 P0 раунда 6. `@uppy/aws-s3` собирал файлы через `Promise.allSettled`
 * (aws-s3/lib/index.js:171), а `@uppy/aws-s3-multipart` — через `Promise.all`
 * (aws-s3-multipart/lib/index.js:518). В ядре все шаги загрузки идут одним `try`, и на
 * отказе загрузчика выполняется `removeUpload(uploadID); throw err`
 * (core/lib/Uppy.js:1731) — то есть постпроцессоры НЕ выполняются и `complete` не
 * эмитится вовсе.
 *
 * Что это значило для приёма: в пачке из тридцати один упавший файл оставлял двадцать
 * девять доехавших без подтверждения — байты в бакете, строк в брони нет. Заодно
 * переставали выполняться уборка доставленных и пересчёт потолка, то есть отключался
 * фикс FT-152 «лимит считается за отправку». При `autoProceed: true` отказ ещё и
 * проглатывается (`Uppy.js:1468`), поэтому сотрудник видел один красный файл и молча
 * не получал остальные.
 *
 * ПОЧЕМУ ПОДКЛАСС, а не подмена загрузчика у живого экземпляра. Первая редакция снимала
 * зарегистрированный `plugin.upload` через `removeUploader`/`addUploader` и переписывала
 * поле. Это работало, но требовало собственного флага идемпотентности и гварда на
 * существование плагина — и ни то, ни другое нельзя было уронить мутацией, то есть обе
 * проверки были декорациями. Штатный шов у Uppy другой: `install()`/`uninstall()` снимают
 * именно `this.upload` (`index.js:583-592`), а конструктор биндит его через цепочку
 * прототипов (`index.js:107`). Значит подкласс с переопределённым `upload()` даёт ту же
 * семантику без мутации чужого объекта и идемпотентно по построению. Идентификатор
 * плагина при этом сохраняется — родитель ставит `this.id = this.opts.id || 'AwsS3Multipart'`
 * (`index.js:90`), поэтому `getPlugin("AwsS3Multipart")` и `guardMultipartAbort` работают
 * как прежде.
 *
 * Отказ здесь гасится, а НЕ теряется: плагин к этому моменту уже сделал
 * `emit('upload-error', file, err)` (`index.js:252`), значит у файла стоит `error`, он
 * попадает в `result.failed` и получает штатную кнопку повтора. Постпроцессор
 * подтверждения такие файлы отсеивает сам.
 */
export class BatchTolerantAwsS3Multipart extends AwsS3Multipart {
  upload(fileIDs) {
    return Promise.resolve(super.upload(fileIDs)).then(
      () => undefined,
      () => undefined
    );
  }
}

export default guardMultipartAbort;
